import clsx from "clsx";
import { useMemo } from "react";
import Select, { components, OptionsOrGroups, SingleValue } from "react-select";
import { ColumnInstance } from "react-table";

interface Option {
  label: string;
  value: string | number;
}

type Choice = Option["value"] | Option;

export const TableHeaderSelectFilter: React.FC<{
  choices: Choice[];
  column: ColumnInstance;
  className?: string;
  multi?: boolean;
}> = ({ choices, column, className, multi = false }) => {
  const options: OptionsOrGroups<Option, never> = useMemo(
    () =>
      choices.map((c) =>
        typeof c === "string"
          ? { label: String(c), value: String(c) }
          : typeof c === "number"
          ? { label: String(c), value: c }
          : c
      ),
    [choices]
  );

  return (
    <Select
      className={clsx("w-full", className)}
      options={options}
      value={options.filter((o) =>
        Array.isArray(column.filterValue)
          ? column.filterValue.includes(o.value)
          : o.value === column.filterValue
      )}
      isClearable={true}
      isMulti={multi}
      isSearchable={false}
      hideSelectedOptions={false}
      menuPortalTarget={document.body}
      classNames={{
        control: (state) => "!min-h-7 !border-0",
        valueContainer: (state) =>
          clsx(
            "!py-0 !text-sm !flex-nowrap",
            state.hasValue
              ? "[&>*]:!text-dm-charcoal-500"
              : "[&>*]:!text-dm-charcoal-500/50"
          ),
        dropdownIndicator: (state) =>
          "h-full w-7 items-center justify-center !p-0 cursor-pointer",
        clearIndicator: (state) =>
          "h-full w-7 items-center justify-center !p-0 cursor-pointer",
        menu: (state) => "!mt-1",
        option: (state) =>
          clsx(
            "!text-sm !py-2 !px-2",
            state.isFocused
              ? "!bg-dm-charcoal-100 !text-dm-charcoal-500"
              : state.isSelected
              ? "!bg-transparent !text-dm-charcoal-800"
              : "!text-dm-charcoal-500"
          ),
        multiValue: (state) =>
          "h-5 rounded !bg-dm-charcoal-100 flex items-center justify-center",
        multiValueRemove: (state) =>
          "w-5 h-full flex items-center justify-center cursor-pointer hover:!bg-dm-charcoal-200 !text-dm-charcoal-500 hover:!text-dm-charcoal-800",
      }}
      components={{
        ValueContainer: ({ children, ...props }) => {
          const value = props.getValue();
          const getText = () => {
            if (props.isMulti) {
              if (value.length === 0) {
                return "Select...";
              }
              if (value.length === 1) {
                return value[0].label;
              }
              return `${value.length} items selected`;
            }
            return value.length ? value[0].label : "Select...";
          };
          return (
            <components.ValueContainer {...props}>
              <div className="w-full cursor-pointer text-left text-dm-charcoal-500">
                {getText()}
                <div className="sr-only">{children}</div>
              </div>
            </components.ValueContainer>
          );
        },
        Option: ({ ...props }) => {
          return (
            <components.Option {...props}>
              <span className="inline-block w-5">
                {props.isSelected && (
                  <i className="far fa-check !text-inherit" />
                )}
              </span>
              {props.children}
            </components.Option>
          );
        },
      }}
      onChange={(newValue) =>
        Array.isArray(newValue)
          ? column.setFilter(newValue.map(({ value }) => value))
          : column.setFilter(
              (newValue as SingleValue<Option>)?.value || undefined
            )
      }
    />
  );
};
